import dayjs from 'dayjs'
import type { RestaurantOverviewQuery, RestaurantOverviewSortKey } from '@/models/restaurant-manager'
import { restaurantOverviewSortKeys } from '@/models/restaurant-manager'

const timePattern = /^\d{2}:\d{2}$/

const toStringArray = (values?: string | string[]) => {
  if (typeof values === 'undefined') {
    return []
  }

  return Array.isArray(values) ? values : [values]
}

const toIntegerArray = (values?: number | string | Array<number | string>) => {
  if (typeof values === 'undefined') {
    return []
  }

  const normalizedValues = Array.isArray(values) ? values : [values]

  return normalizedValues.map(value => (typeof value === 'string' ? Number(value) : value)).filter(value => Number.isInteger(value))
}

const uniqueStrings = (values?: string | string[]) => Array.from(new Set(toStringArray(values).filter(Boolean)))
const uniqueNumbers = (values?: number | string | Array<number | string>) =>
  Array.from(new Set(toIntegerArray(values))).sort((a, b) => a - b)

const isRestaurantOverviewSortKey = (value: string | undefined): value is RestaurantOverviewSortKey =>
  !!value && restaurantOverviewSortKeys.includes(value as RestaurantOverviewSortKey)

export const createDefaultRestaurantOverviewQuery = (date = dayjs().format('YYYY-MM-DD')): RestaurantOverviewQuery => ({
  date,
  subjectIds: [],
  spaceIds: [],
  nightIndexes: [],
  sortKey: 'roomNumber',
  sortOrder: 'asc',
})

export const normalizeRestaurantOverviewQuery = (
  query: Omit<Partial<RestaurantOverviewQuery>, 'subjectIds' | 'spaceIds' | 'nightIndexes'> &
    Pick<RestaurantOverviewQuery, 'date'> & {
      subjectIds?: string | string[]
      spaceIds?: string | string[]
      nightIndexes?: number | string | Array<number | string>
    },
): RestaurantOverviewQuery => ({
  date: query.date,
  subjectIds: uniqueStrings(query.subjectIds),
  spaceIds: uniqueStrings(query.spaceIds),
  nightIndexes: uniqueNumbers(query.nightIndexes),
  startTimeFrom: query.startTimeFrom && timePattern.test(query.startTimeFrom) ? query.startTimeFrom : undefined,
  startTimeTo: query.startTimeTo && timePattern.test(query.startTimeTo) ? query.startTimeTo : undefined,
  sortKey: isRestaurantOverviewSortKey(query.sortKey) ? query.sortKey : 'roomNumber',
  sortOrder: query.sortOrder === 'desc' ? 'desc' : 'asc',
})

export const countUnconfirmedRestaurantCancellations = (groups: Array<{ cancellations: Array<{ isConfirmed: boolean }> }>): number => {
  return groups.reduce((total, group) => {
    return total + group.cancellations.filter(cancellation => !cancellation.isConfirmed).length
  }, 0)
}
